import { IDaySchedule, IPlanRange } from "@services/getMinerList";
import { formatWithoutTz } from "./helpers";
import { IntlFormatters, IntlProvider } from "react-intl";

function convertTimeToMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

export function isEmptyPeriod(value: [string, string]): boolean {
  if (value[0] === "00:00" && value[1] === "00:00") {
    return true;
  }
  return false;
}

export function isEmptyPeriods(values: IPlanRange[]): boolean {
  if (values.length === 1) {
    return isEmptyPeriod(values[0].time);
  }
  return false;
}

export function areRangeEqual(arr1: IPlanRange[], arr2: IPlanRange[]): boolean {
  if (arr1 === arr2) return true;  // Если это один и тот же массив
  if (arr1.length !== arr2.length) return false;

  for (let i = 0; i < arr1.length; i++) {
    const sub1 = arr1[i];
    const sub2 = arr2[i];
    if (sub1.time === sub2.time) continue;  // Пропускаем одинаковые подмассивы
    if (sub1.time.length !== sub2.time.length) return false;

    for (let j = 0; j < sub1.time.length; j++) {
      if (sub1.time[j] !== sub2.time[j]) return false;
    }
  }
  return true;
}

export function arePeriodsEqual(arr1: [string, string][], arr2: [string, string][]): boolean {
  if (arr1 === arr2) return true;  // Если это один и тот же массив
  if (arr1.length !== arr2.length) return false;

  for (let i = 0; i < arr1.length; i++) {
    const sub1 = arr1[i];
    const sub2 = arr2[i];
    if (sub1 === sub2) continue;  // Пропускаем одинаковые подмассивы
    if (sub1.length !== sub2.length) return false;

    for (let j = 0; j < sub1.length; j++) {
      if (sub1[j] !== sub2[j]) return false;
    }
  }
  return true;
}

// сортируем только корректные периоды, с одним переходом!
// после validateTimePeriods()
export function sortPeriods(
  periods: IPlanRange[]
): IPlanRange[] {
  let result: IPlanRange[] = [];
  for (let i = periods.length - 1; i >= 0; i--) {
    if (periods[i].time[0] === "") {
      result.push(...periods.splice(i, 1));
    }
    if (periods[i].time[1] === "") {
      result = [...periods.splice(i, 1), ...result];
    }
  }
  if (result.length === 2) {
    const sorted = [...periods].sort(sortFun);
    let min = 1440; // (23 * 60 + 59) + 1 => больше этого нет времени
    let idx = -1;
    for (let i = 0; i < sorted.length; i++) {
      const delta = convertTimeToMinutes(result[0].time[0]) - convertTimeToMinutes(sorted[i].time[1]);
      if (delta > 0 && delta < min) {
        min = delta;
        idx = i + 1;
      }
    }
    if (idx > -1) {
      return [
        ...sorted.slice(0, idx),
        ...result,
        ...sorted.slice(idx)
      ]
    }
    return result;
  } else {
    return [...periods].sort(sortFun);
  }

  function sortFun(a: IPlanRange, b: IPlanRange): number {
    return convertTimeToMinutes(a.time[0]) - convertTimeToMinutes(b.time[0]);
  }
}

export function isInRange(ranges: string[][]): boolean {
  const now = new Date();
  const currentHours = now.getHours();
  const currentMinutes = now.getMinutes();
  const currentTime = currentHours * 60 + currentMinutes;
  for (const range of ranges) {
    const [startTime, endTime] = range;
    const startTotalMinutes = convertTimeToMinutes(startTime);
    const endTotalMinutes = convertTimeToMinutes(endTime);
    if (startTotalMinutes <= endTotalMinutes) {
      if (currentTime >= startTotalMinutes && currentTime <= endTotalMinutes) {
        return true;
      }
    } else {
      if (currentTime >= startTotalMinutes || currentTime <= endTotalMinutes) {
        return true;
      }
    }
  }
  return false;
}

export function getRangesFor(
  date: Date,
  schedule: IDaySchedule[]
): IPlanRange[] {
  const current: Omit<IDaySchedule, "color">[] = [];
  const dayIndex = date.getDay().toString();
  const dateStr = formatWithoutTz(date);
  let hasExclude = false;
  schedule.forEach(p => {
    if (p.day === dayIndex || p.day === dateStr) {
      if (isEmptyPeriods(p.range)) {
        hasExclude = true;
      }
      current.push({
        day: p.day,
        range: p.range,
      });
    }
  });
  if (current.length === 0) {
    return [];
  }
  const day = current.find(c => c.day.length === "DD/MM/YYYY".length && !isEmptyPeriods(c.range));
  if (day) {
    return [...day.range];
  }
  const exclude = current.find(c => c.day.length === "DD/MM/YYYY".length && isEmptyPeriods(c.range));
  if (exclude) {
    return [];
  }
  const weekDay = current.find(c => c.day.length === 1);
  if (weekDay) {
    return [...weekDay.range];
  }
  return [];
}

export function isTimeInPeriods(date: Date, ranges: IPlanRange[]): boolean {
  const currentMinutes = date.getHours() * 60 + date.getMinutes();
  for (const range of ranges) {
    const [startTime, endTime] = range.time;
    const start = convertTimeToMinutes(startTime);
    const end = convertTimeToMinutes(endTime);
    if (start <= end) {
      if (currentMinutes >= start && currentMinutes <= end) {
        return true;
      }
    } else {
      if (currentMinutes >= start || currentMinutes <= end) {
        return true;
      }
    }
  }
  return false;
}

function isValidTimeFormat(time: string): boolean {
  return /^([01]\d|2[0-3]):([0-5]\d)$/.test(time);
}

function isPeriodValid(start: string, end: string): boolean {
  return compareTime(start, end) <= 0;
}

function compareTime(a: string, b: string): number {
  const [aHours, aMinutes] = a.split(":").map(Number);
  const [bHours, bMinutes] = b.split(":").map(Number);

  if (aHours !== bHours) return aHours - bHours;
  return aMinutes - bMinutes;
}

function normalizePeriods(periods: IPlanRange[]): IPlanRange[] {
  return periods.filter(period => period.time[0] !== "" && period.time[1] !== "");
}

export function validateTimePeriods(
  periods: IPlanRange[],
  intl: Pick<IntlFormatters, "formatMessage">
): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  // Проверка на пустые периоды
  if (periods.length === 0) {
    return { isValid: true, errors };
  }
  let crossedDayCount = 0;
  let hasEmptyStart = false;
  let hasEmptyEnd = false;
  // Проверка формата времени и корректности периодов
  for (let i = 0; i < periods.length; i++) {
    const { time: [start, end] } = periods[i];

    // Проверка на переходы между днями
    // if (start === "" && end === "") {
    //   errors.push(intl.formatMessage({
    //     id: "manage.plan_info.error.two_empty"
    //   }, {
    //     period: `[${i}]`
    //   })); // `Период [${i}] содержит два пустых значения`);
    //   continue;
    // }

    if (start === "") {
      if (hasEmptyStart) {
        errors.push(intl.formatMessage({
          id: "manage.plan_info.error.multiple_cross_empty_start"
        })); // `Обнаружено более одного перехода на следующий день (пустой старт)`);
      }
      hasEmptyStart = true;
      // if (!hasEmptyEnd) {
      //   errors.push(intl.formatMessage({
      //     id: "manage.plan_info.error.incorrect_cross_next_day"
      //   })); // `Обнаружен некорректный переход на следующий день (старт без конца)`);
      // }
    }

    if (end === "") {
      if (hasEmptyEnd) {
        errors.push(intl.formatMessage({
          id: "manage.plan_info.error.multiple_cross_empty_end"
        })); // `Обнаружено более одного перехода на следующий день (пустой конец)`);
      }
      hasEmptyEnd = true;
    }

    if (hasEmptyStart && hasEmptyEnd) {
      if (crossedDayCount > 0 && crossedDayCount < 2) {
        errors.push(intl.formatMessage({
          id: "manage.plan_info.error.incorrect_cross_multiple"
        })); // "Некорректный переход между днями: не может быть несколько переходов");
      }
      crossedDayCount++;
      hasEmptyStart = false;
      hasEmptyEnd = false;
    }

    // Проверка формата времени
    if (start !== "" && !isValidTimeFormat(start)) {
      errors.push(intl.formatMessage({
        id: "manage.plan_info.error.incorrect_format_start"
      }, {
        period: `[${i}]`,
        start
      })); // `Неверный формат времени начала в периоде [${i}]: ${start}`);
    }

    if (end !== "" && !isValidTimeFormat(end)) {
      errors.push(intl.formatMessage({
        id: "manage.plan_info.error.incorrect_format_end"
      }, {
        period: `[${i}]`,
        end
      })); // `Неверный формат времени окончания в периоде [${i}]: ${end}`);
    }

    // Проверка на отрицательный период (если оба времени указаны)
    if (start !== "" && end !== "" && !isPeriodValid(start, end)) {
      errors.push(intl.formatMessage({
        id: "manage.plan_info.error.incorrect_period_greater"
      }, {
        period: `[${i}]`,
        start,
        end
      })); // `Неверный период в [${i}]: ${start} > ${end}`);
    }
  }

  // if (hasEmptyStart || hasEmptyEnd) {
  //   errors.push(intl.formatMessage({
  //     id: "manage.plan_info.error.incorrect_periods_not_close"
  //   })); // `Присутствуют незакрытые периоды`);
  // }

  // Проверка на пересечение периодов
  // const normalizedPeriods = normalizePeriods(periods);
  // for (let i = 1; i < normalizedPeriods.length; i++) {
  //   const prevEnd = normalizedPeriods[i - 1][1];
  //   const currStart = normalizedPeriods[i][0];
  //   if (compareTime(prevEnd, currStart) > 0) {
  //     errors.push(intl.formatMessage({
  //       id: "manage.plan_info.error.crossed_periods"
  //     }, {
  //       periods0: `[${periods[i - 1]}]`,
  //       periods1: `[${periods[i]}]`,
  //     })); // Периоды пересекаются: ${periods[i - 1]} и ${periods[i]}`);
  //   }
  // }

  return {
    isValid: errors.length === 0,
    errors
  };
}